import React, { useState, useEffect, useRef, memo } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { IconButton } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { VOICE_AGORA_APP_ID } from '../constants/commonConstants';
import { getAgoraToken } from '../services/VoiceChatService';
import { useTranslation } from 'react-i18next';
import { acquireAudioSession } from '../services/AudioSession';
import { useSubscription } from '../context/SubscriptionContext';
import SubscriptionDialog from './SubscriptionDialog';
import { useToast } from './Toast';
import SignalRService from '../services/SignalRService';

const RoomVoiceChat = memo(({ roomId, playerId, hasOtherPlayer = true }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [micOn, setMicOn] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(false);
  const [token, setToken] = useState(null);
  const tokenIssuedAt = useRef(0);
  const [session, setSession] = useState(null);
  const { feature } = useSubscription();
  const microphone = feature('poker', 'microphone');
  const speaker = feature('poker', 'speaker');
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const effectiveMic = micOn && microphone.allowed && speaker.allowed && hasOtherPlayer;
  const effectiveSpeaker = speakerOn && speaker.allowed && hasOtherPlayer;
  const remoteAudioTracks = useRef({});
  const speakerEnabled = useRef(speakerOn);
  speakerEnabled.current = effectiveSpeaker;
  const enabled = effectiveMic || effectiveSpeaker;
  useEffect(() => {
    const publish = () => SignalRService.setVoiceStatus(effectiveMic, effectiveSpeaker);
    window.addEventListener('pwf-connection-state', publish);
    publish();
    return () => window.removeEventListener('pwf-connection-state', publish);
  }, [effectiveMic, effectiveSpeaker]);
  useEffect(() => { if (!microphone.allowed) setMicOn(false); }, [microphone.allowed]);
  useEffect(() => { if (!speaker.allowed) { setSpeakerOn(false); setMicOn(false); } }, [speaker.allowed]);
  useEffect(() => {
    if (!hasOtherPlayer) {
      setMicOn(false);
      setSpeakerOn(false);
    }
  }, [hasOtherPlayer]);

  useEffect(() => {
    const stopMicrophone = () => setMicOn(false);
    const stopHiddenMicrophone = () => {
      if (document.visibilityState === 'hidden') stopMicrophone();
    };
    document.addEventListener('visibilitychange', stopHiddenMicrophone);
    window.addEventListener('blur', stopMicrophone);
    window.addEventListener('pagehide', stopMicrophone);
    return () => {
      document.removeEventListener('visibilitychange', stopHiddenMicrophone);
      window.removeEventListener('blur', stopMicrophone);
      window.removeEventListener('pagehide', stopMicrophone);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    setToken(null);
    if (!hasOtherPlayer || (!microphone.allowed && !speaker.allowed)) return () => { disposed = true; };
    // Authorize the capability, not the current toggle: switching a microphone
    // on/off must not tear down the listener's existing voice session.
    getAgoraToken(roomId, playerId, microphone.allowed).then(
      value => { if (!disposed) { tokenIssuedAt.current = Date.now(); setToken(value); } },
      () => { if (!disposed) setToken(null); }
    );
    return () => { disposed = true; };
  }, [roomId, playerId, microphone.allowed, speaker.allowed, hasOtherPlayer]);

  // Each enabled session owns its client. A late join/subscribe cannot attach
  // tracks to the next room, and mic/speaker toggles don't duplicate joins.
  useEffect(() => {
    if (!enabled || !token || !roomId || !playerId) return;
    let disposed = false;
    const rtc = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
    const tracks = {};
    remoteAudioTracks.current = tracks;
    const leave = () => Promise.resolve(rtc.leave()).catch(() => {});
    let renewing = false;
    const renew = async () => {
      if (disposed || renewing || document.visibilityState === 'hidden') return;
      renewing = true;
      try {
        const nextToken = await getAgoraToken(roomId, playerId, microphone.allowed);
        if (!disposed && nextToken) await rtc.renewToken(nextToken);
      } catch (_) {
        // No account logout or room reconnect for an audio-token outage.
      } finally { renewing = false; }
    };
    rtc.on('token-privilege-will-expire', renew);
    const tokenTimer = setInterval(renew, 30000);
    const visible = () => { if (document.visibilityState !== 'hidden') void renew(); };
    document.addEventListener('visibilitychange', visible);
    rtc.on('user-published', async (user, mediaType) => {
      if (mediaType !== 'audio') return;
      try {
        await rtc.subscribe(user, mediaType);
        if (disposed) return;
        if (user.audioTrack) {
          tracks[user.uid] = user.audioTrack;
          if (speakerEnabled.current) user.audioTrack.play();
        }
      } catch (error) {
        if (!disposed) console.error('Voice subscription failed:', error);
      }
    });
    rtc.on('user-unpublished', (user, mediaType) => {
      if (mediaType !== 'audio') return;
      tracks[user.uid]?.stop();
      delete tracks[user.uid];
    });
    (async () => {
      try {
        // A token prefetched while both toggles were off may have expired.
        const joinToken = Date.now() - tokenIssuedAt.current < 30000 ? token : await getAgoraToken(roomId, playerId, microphone.allowed);
        if (disposed) return;
        await rtc.join(VOICE_AGORA_APP_ID, roomId, joinToken, playerId);
        if (disposed) { await leave(); return; }
        setSession(rtc);
      } catch (error) {
        if (!disposed) console.error('Voice connection failed.');
      }
    })();
    return () => {
      disposed = true;
      clearInterval(tokenTimer);
      document.removeEventListener('visibilitychange', visible);
      setSession(current => current === rtc ? null : current);
      rtc.removeAllListeners();
      Object.values(tracks).forEach(track => track.stop());
      if (remoteAudioTracks.current === tracks) remoteAudioTracks.current = {};
      void leave();
    };
  }, [enabled, roomId, playerId, token, microphone.allowed]);

  useEffect(() => {
    if (!effectiveMic || !session) return;
    let disposed = false;
    let track;
    const releaseAudioSession = acquireAudioSession(true);
    (async () => {
      try {
        track = await AgoraRTC.createMicrophoneAudioTrack();
        if (disposed) { track.close(); return; }
        await session.publish([track]);
        if (disposed) {
          await session.unpublish([track]).catch(() => {});
          track.close();
        }
      } catch (error) {
        track?.close();
        releaseAudioSession();
        if (!disposed) console.error('Microphone initialization failed:', error);
      }
    })();
    return () => {
      disposed = true;
      if (track) {
        track.close();
        void session.unpublish([track]).catch(() => {});
      }
      releaseAudioSession();
    };
  }, [effectiveMic, session]);

  useEffect(() => {
    Object.values(remoteAudioTracks.current).forEach(track => {
      if (effectiveSpeaker) track.play();
      else track.stop();
    });
  }, [effectiveSpeaker]);

  const explainNoOtherPlayer = () => showToast({ message: t('menuBar.voiceNeedsOtherPlayer'), variant: 'info' });
  const toggleMic = () => {
    if (!hasOtherPlayer) { explainNoOtherPlayer(); return; }
    if (microphone.allowed && speaker.allowed) {
      setMicOn(value => {
        const next = !value;
        if (next) setSpeakerOn(true);
        return next;
      });
    }
    else if (microphone.locked || speaker.locked) setSubscribeOpen(true);
  };
  const toggleSpeaker = () => {
    if (!hasOtherPlayer) { explainNoOtherPlayer(); return; }
    if (speaker.allowed) setSpeakerOn(value => {
      const next = !value;
      if (!next) setMicOn(false);
      return next;
    });
    else if (speaker.locked) setSubscribeOpen(true);
  };


  return (
    <div className="voice-section-wrapper">
      <div className="voice-section">
        {microphone.show && <IconButton
          size="large"
          aria-label={t('menuBar.microphone')}
          title={t(!hasOtherPlayer ? 'menuBar.voiceNeedsOtherPlayer' : microphone.locked || speaker.locked ? 'subscription.required' : 'menuBar.microphone')}
          aria-pressed={effectiveMic}
          aria-disabled={!hasOtherPlayer || !microphone.enabled || !speaker.enabled}
          disabled={!hasOtherPlayer ? false : !microphone.enabled || !speaker.enabled}
          sx={!hasOtherPlayer ? { opacity: 0.45 } : undefined}
          color="inherit"
          className="top-bar-control btn-mic"
          onClick={toggleMic}
        >
          {effectiveMic ? <MicIcon /> : <MicOffIcon />}
        </IconButton>}
        {speaker.show && <IconButton
          size="large"
          aria-label={t('menuBar.speaker')}
          title={t(!hasOtherPlayer ? 'menuBar.voiceNeedsOtherPlayer' : speaker.locked ? 'subscription.required' : 'menuBar.speaker')}
          aria-pressed={effectiveSpeaker}
          aria-disabled={!hasOtherPlayer || !speaker.enabled}
          disabled={!hasOtherPlayer ? false : !speaker.enabled}
          sx={!hasOtherPlayer ? { opacity: 0.45 } : undefined}
          color="inherit"
          className="top-bar-control btn-volume"
          onClick={toggleSpeaker}
        >
          {effectiveSpeaker ? <VolumeUpIcon /> : <VolumeOffIcon />}
        </IconButton>}
      </div>
      <SubscriptionDialog open={subscribeOpen} onClose={() => setSubscribeOpen(false)} />
    </div>
  );
});

export default RoomVoiceChat;




// import React, { useState, useEffect, useRef, memo } from 'react';
// import AgoraRTC from 'agora-rtc-sdk-ng';
// import { IconButton } from '@mui/material';
// import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// import VolumeOffIcon from '@mui/icons-material/VolumeOff';
// import MicIcon from '@mui/icons-material/Mic';
// import MicOffIcon from '@mui/icons-material/MicOff';
// import { VOICE_AGORA_APP_ID } from '../constants/commonConstants';
// import { getAgoraToken } from '../services/VoiceChatService';

// const RoomVoiceChat = memo(({ roomId, playerId }) => {
//   const [micOn, setMicOn] = useState(false);
//   const [speakerOn, setSpeakerOn] = useState(false);
//   const [triggerAction, setTriggerAction] = useState(false);
//   const [token, setToken] = useState(null);
//   const client = useRef(null);
//   const localAudioTrack = useRef(null);
//   const remoteAudioTracks = useRef({});
//   const appId = VOICE_AGORA_APP_ID;

//     const triggerMicAndSpeaker = () => {
//     // setTimeout(() => {
//       setTriggerAction(prev => !prev);
//     // }, 500);
//   }

//   // Disable Agora SDK logs
//   console.log = function () {};
//   console.warn = function () {};
//   useEffect(() => {
//     AgoraRTC.setLogLevel(0);

//     const fetchToken = async () => {
//         if (!roomId || !playerId) return;
//         try {
//           const response = await getAgoraToken(roomId, playerId);
//           setToken(response);
//         } catch (err) {
//           setToken(null);
//         }
//       };

//       fetchToken();
//   }, []);

//   const leaveChannel = () => {
//     if (client.current) {
//       client.current.leave();
//       if (localAudioTrack.current) {
//         localAudioTrack.current.close();
//         localAudioTrack.current = null; // Avoid stale references
//       }
//       Object.values(remoteAudioTracks.current).forEach((track) => track.stop());
//       remoteAudioTracks.current = {};
//     }
//   }

//   // Handle room join logic separately from mic/speaker toggle
//   useEffect(() => {
//     if (!roomId || !playerId || !token) {
//       return;
//     }

//     client.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

//     client.current.on('user-published', async (user, mediaType) => {
//         // console.error("user-published.......................", user, mediaType);
//       if (mediaType === 'audio') {
//         try {
//           await client.current.subscribe(user, mediaType);
//           const remoteAudioTrack = user.audioTrack;
//           if (remoteAudioTrack) {
//             remoteAudioTracks.current[user.uid] = remoteAudioTrack;
//             // Play the remote audio if speaker is on
//             if (speakerOn) {
//               remoteAudioTrack.play();
//             }
//           }
//         } catch (error) {
//           console.error('Error subscribing to user:', error);
//         }
//       }
//       triggerMicAndSpeaker();
//     });

//     client.current.on('user-unpublished', (user, mediaType) => {
//         // console.error("user-unpublished.......................", user, mediaType);
//       if (mediaType === 'audio') {
//         const remoteAudioTrack = remoteAudioTracks.current[user.uid];
//         if (remoteAudioTrack) {
//           remoteAudioTrack.stop();
//           delete remoteAudioTracks.current[user.uid];
//         }
//       }
//       triggerMicAndSpeaker();
//     });

//     return () => {
//       leaveChannel();
//     };
//   }, [roomId, playerId, token]);

//   const joinChannel = async () => {
//     try {
//       await client.current.join(appId, roomId, token, playerId);
//         await initializeLocalAudioTrack();
//       triggerMicAndSpeaker();
//     } catch (error) {
//       // console.error('Failed to join the room:', error);
//     }
//   };

//   const initializeLocalAudioTrack = async () => {
//     try {
//       localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();
//       await client.current.publish([localAudioTrack.current]);
//       localAudioTrack.current.setMuted(!micOn);
//     } catch (error) {
//       console.error('Error initializing local audio track:', error);
//     }
//   };

//   const handleChannelJoinOrLeave = async () => {
//     if(speakerOn || micOn){
//       await joinChannel();
//     }else{
//       leaveChannel();
//     }
//   }

//   // Handle mic toggle with delay
//   const toggleMic = () => {
//     setMicOn((prevState) => !prevState);
//   };

//   // Handle speaker toggle with delay
//   const toggleSpeaker = () => {
//     setSpeakerOn((prevState) => !prevState);
//   };
  
//   useEffect(() => {
//     handleChannelJoinOrLeave();
//     if (speakerOn) {
//       Object.values(remoteAudioTracks.current).forEach((track) => {
//         if (track) {
//           track.play();
//         }
//       });
//     } else {
//       Object.values(remoteAudioTracks.current).forEach((track) => {
//         if (track) {
//           track.stop();
//         }
//       });
//     }
//   }, [speakerOn]);

//   useEffect(() => {
//     handleChannelJoinOrLeave();
//     if (localAudioTrack.current) {
//       localAudioTrack.current.setMuted(!micOn);
//     }
//   }, [micOn]);

//   useEffect(() => {
//     if (localAudioTrack.current) {
//       localAudioTrack.current.setMuted(!micOn);
//     }
//     if (speakerOn) {
//       Object.values(remoteAudioTracks.current).forEach((track) => {
//         if (track) {
//           track.play();
//         }
//       });
//     }
//   }, [triggerAction]);

//   return (
//     <div className="voice-section-wrapper">
//       <div className="voice-section">
//         <IconButton
//           size="large"
//           aria-label="volume"
//           color="inherit"
//           className="btn-volume"
//           onClick={toggleSpeaker}
//         >
//           {speakerOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
//         </IconButton>
//         <IconButton
//           size="large"
//           aria-label="mic"
//           color="inherit"
//           className="btn-mic"
//           onClick={toggleMic}
//         >
//           {micOn ? <MicIcon /> : <MicOffIcon />}
//         </IconButton>
//       </div>
//     </div>
//   );
// });

// export default RoomVoiceChat;



// import React, { useState, useEffect, useRef, memo } from 'react';
// import AgoraRTC from 'agora-rtc-sdk-ng';
// import { IconButton } from '@mui/material';
// import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// import VolumeOffIcon from '@mui/icons-material/VolumeOff';
// import MicIcon from '@mui/icons-material/Mic';
// import MicOffIcon from '@mui/icons-material/MicOff';
// import { VOICE_AGORA_APP_ID } from '../constants/commonConstants';
// import { getAgoraToken } from '../services/VoiceChatService';

// const RoomVoiceChat = memo(({ roomId, playerId }) => {
//   const [micOn, setMicOn] = useState(false);
//   const [speakerOn, setSpeakerOn] = useState(false);
//   const [triggerAction, setTriggerAction] = useState(false);
//   const [token, setToken] = useState(null);
//   const client = useRef(null);
//   const localAudioTrack = useRef(null);
//   const remoteAudioTracks = useRef({});
//   const appId = VOICE_AGORA_APP_ID;

//     const triggerMicAndSpeaker = () => {
//     // setTimeout(() => {
//       setTriggerAction(prev => !prev);
//     // }, 500);
//   }

//   // Disable Agora SDK logs
//   console.log = function () {};
//   console.warn = function () {};
//   useEffect(() => {
//     AgoraRTC.setLogLevel(0);

//     const fetchToken = async () => {
//         if (!roomId || !playerId) return;
//         try {
//           const response = await getAgoraToken(roomId, playerId);
//           setToken(response);
//         } catch (err) {
//           setToken(null);
//         }
//       };

//       fetchToken();
//   }, []);

//   // Handle room join logic separately from mic/speaker toggle
//   useEffect(() => {
//     if (!roomId || !playerId || !token) {
//       return;
//     }

//     client.current = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });

//     const joinRoom = async () => {
//       try {
//         await client.current.join(appId, roomId, token, playerId);
//         await initializeLocalAudioTrack();
//         triggerMicAndSpeaker();
//       } catch (error) {
//         // console.error('Failed to join the room:', error);
//       }
//     };

//     const initializeLocalAudioTrack = async () => {
//       try {
//         localAudioTrack.current = await AgoraRTC.createMicrophoneAudioTrack();
//         await client.current.publish([localAudioTrack.current]);
//         localAudioTrack.current.setMuted(false); // Initialize with mic state
//       } catch (error) {
//         console.error('Error initializing local audio track:', error);
//       }
//     };

//     client.current.on('user-published', async (user, mediaType) => {
//         // console.error("user-published.......................", user, mediaType);
//       if (mediaType === 'audio') {
//         try {
//           await client.current.subscribe(user, mediaType);
//           const remoteAudioTrack = user.audioTrack;
//           if (remoteAudioTrack) {
//             remoteAudioTracks.current[user.uid] = remoteAudioTrack;
//             // Play the remote audio if speaker is on
//             if (speakerOn) {
//               remoteAudioTrack.play();
//             }
//           }
//         } catch (error) {
//           console.error('Error subscribing to user:', error);
//         }
//       }
//       triggerMicAndSpeaker();
//     });

//     client.current.on('user-unpublished', (user, mediaType) => {
//         // console.error("user-unpublished.......................", user, mediaType);
//       if (mediaType === 'audio') {
//         const remoteAudioTrack = remoteAudioTracks.current[user.uid];
//         if (remoteAudioTrack) {
//           remoteAudioTrack.stop();
//           delete remoteAudioTracks.current[user.uid];
//         }
//       }
//       triggerMicAndSpeaker();
//     });

//     joinRoom();

//     return () => {
//       if (client.current) {
//         client.current.leave();
//         if (localAudioTrack.current) {
//           localAudioTrack.current.close();
//           localAudioTrack.current = null; // Avoid stale references
//         }
//         Object.values(remoteAudioTracks.current).forEach((track) => track.stop());
//         remoteAudioTracks.current = {};
//       }
//     };
//   }, [roomId, playerId, token]); // Only depends on roomId and playerId to join the room

//   // Handle mic toggle with delay
//   const toggleMic = () => {
//     setMicOn((prevState) => {
//       const newState = !prevState;
//       if (localAudioTrack.current) {
//         localAudioTrack.current.setMuted(!newState);
//       } else {
//         // console.warn('Local audio track is not initialized');
//       }
//       return newState;
//     });
//   };

//   // Handle speaker toggle with delay
//   const toggleSpeaker = () => {
//     setSpeakerOn((prevState) => {
//       const newState = !prevState;
//       // Only start playing remote tracks after they are available
//       if (newState) {
//         Object.values(remoteAudioTracks.current).forEach((track) => {
//           if (track) {
//             track.play();
//           }
//         });
//       } else {
//         Object.values(remoteAudioTracks.current).forEach((track) => {
//           if (track) {
//             track.stop();
//           }
//         });
//       }
//       return newState;
//     });
//   };

//   // Ensure tracks are initialized correctly after room join
//   useEffect(() => {
//     // console.error("Change......................", micOn, speakerOn)
//     if (localAudioTrack.current) {
//       localAudioTrack.current.setMuted(!micOn); // Set mic state correctly after join
//     }
//     if (speakerOn) {
//       // Ensure speaker state is correct after joining
//       Object.values(remoteAudioTracks.current).forEach((track) => {
//         if (track) {
//           track.play();
//         }
//       });
//     }
//   }, [triggerAction]); // Ensure state updates after mic/speaker state changes

//   return (
//     <div className="voice-section-wrapper">
//       <div className="voice-section">
//         <IconButton
//           size="large"
//           aria-label="volume"
//           color="inherit"
//           className="btn-volume"
//           onClick={toggleSpeaker}
//         >
//           {speakerOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
//         </IconButton>
//         <IconButton
//           size="large"
//           aria-label="mic"
//           color="inherit"
//           className="btn-mic"
//           onClick={toggleMic}
//         >
//           {micOn ? <MicIcon /> : <MicOffIcon />}
//         </IconButton>
//       </div>
//     </div>
//   );
// });

// export default RoomVoiceChat;
